
import React, { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';
import { useLanguage } from './LanguageContext';
import { TRANSLATIONS } from '../constants';
import { ViewType } from '../types';

interface NavBarProps {
  onNavigate: (view: ViewType) => void;
  currentView: ViewType;
}

const NavBar: React.FC<NavBarProps> = ({ onNavigate, currentView }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { language } = useLanguage();
  const t = TRANSLATIONS[language];

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: language === 'EN' ? 'Home' : 'Kreu', view: 'home' as ViewType },
    { name: language === 'EN' ? 'Firm' : 'Firma', view: 'story' as ViewType },
    { name: language === 'EN' ? 'Practices' : 'Praktikat', view: 'home' as ViewType, hash: '#practices' },
    { name: language === 'EN' ? 'Contact' : 'Kontakti', view: 'home' as ViewType, hash: '#contact' },
  ];

  const handleClick = (link: typeof navLinks[0]) => {
    if (link.hash && currentView === 'home') {
      setIsOpen(false);
      document.querySelector(link.hash)?.scrollIntoView({ behavior: 'smooth' });
    } else {
      onNavigate(link.view);
      setIsOpen(false);
    }
  };

  return (
    <nav className={`sticky top-[40px] z-50 transition-all duration-500 ${scrolled ? 'bg-white/95 backdrop-blur-md shadow-xl py-2' : 'bg-white py-4'}`}>
      <div className="max-w-7xl mx-auto px-6 lg:px-12 flex justify-between items-center">
        <div className="flex items-center">
          <button onClick={() => onNavigate('home')} className="group flex flex-col items-start leading-tight">
            <div className="text-[10px] font-bold tracking-[0.2em] text-[#c5a059] uppercase opacity-80 mb-0.5">
              Law Offices of
            </div>
            <div className="text-xl md:text-2xl font-serif font-black tracking-tight text-gray-900 group-hover:text-[#c5a059] transition-colors flex items-baseline">
              AKIL TIRANA<span className="text-[#c5a059] font-light ml-1.5 italic">Esq.</span>
            </div>
          </button>
        </div>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center gap-10">
          <div className="flex items-center gap-8 mr-4">
            {navLinks.map((link) => (
              <button 
                key={link.name} 
                onClick={() => handleClick(link)}
                className="text-gray-800 hover:text-[#c5a059] font-bold transition-all duration-300 tracking-widest text-[11px] uppercase relative group"
              >
                {link.name}
                <span className={`absolute -bottom-1 left-0 h-[1.5px] bg-[#c5a059] transition-all duration-300 ${currentView === link.view && !link.hash ? 'w-full' : 'w-0 group-hover:w-full'}`}></span>
              </button>
            ))}
          </div>
          <button 
            onClick={() => {
              if (currentView === 'home') {
                document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
              } else {
                onNavigate('home');
                setTimeout(() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' }), 100);
              }
            }}
            className="bg-[#0a0a0a] text-white px-8 py-3.5 text-[11px] font-bold tracking-widest hover:bg-[#c5a059] transition-all duration-300 shadow-lg hover:-translate-y-0.5"
          >
            {t.heroCtaPrimary}
          </button>
        </div>

        {/* Mobile Navigation Toggle */}
        <button className="md:hidden text-gray-900 p-2" onClick={() => setIsOpen(!isOpen)}>
          {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <div className="md:hidden fixed inset-x-0 top-[100%] bg-white border-t border-gray-100 shadow-2xl animate-in slide-in-from-top duration-300 z-50 overflow-hidden">
          <div className="flex flex-col p-6 gap-6 bg-white">
            {navLinks.map((link) => (
              <button 
                key={link.name} 
                onClick={() => handleClick(link)}
                className="text-gray-800 hover:text-[#c5a059] font-bold py-3 text-sm tracking-widest uppercase border-b border-gray-50 flex justify-between items-center group text-left"
              >
                {link.name}
                <X className="w-4 h-4 opacity-0 group-hover:opacity-20 transition-opacity rotate-45" />
              </button>
            ))}
            <button 
              onClick={() => { 
                onNavigate('home'); 
                setIsOpen(false);
                setTimeout(() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' }), 100);
              }}
              className="bg-[#0a0a0a] text-white py-4 text-center font-bold tracking-widest text-sm shadow-xl"
            >
              {t.heroCtaPrimary}
            </button>
          </div>
        </div>
      )}
    </nav>
  );
};

export default NavBar;
